// apps/roleforge-app/src/components/cap/CapExceededModal.tsx
// Plan 3b-ii — Phase 3 (§8.6): Fixed-overlay modal shown when a cap error occurs.
// Plan 3e-ii Task 7.6: Adds `kind="invite"` variant for invitation-cap with an
// activation-trial CTA (per §8.5 / §13). The activation path is the only
// allowed CTA — never an agency CTA.

import { Link } from 'react-router-dom';
import { getCapCopy, type CapError } from '../../lib/cap-error';
import { useDialogFocus } from '../../lib/use-dialog-focus';

interface BaseProps {
  onClose: () => void;
}

interface AuditProps extends BaseProps {
  kind?: 'audit';
  error: CapError;
}

interface InviteProps extends BaseProps {
  kind: 'invite';
  auditId: string;
  /**
   * Issue #1324 — the parsed 402 so the copy can state the REAL org-wide
   * numbers. Optional: a caller with no error object still gets honest,
   * numberless copy rather than the old "for this audit" / "future audits"
   * claims, which were both wrong (the counter is org-wide and lifetime).
   */
  error?: CapError;
}

type Props = AuditProps | InviteProps;

/**
 * Issue #1324: the invite variant used to promise "You've used your free
 * invitation slots for this audit" + "Activate a VibeControl trial to invite
 * unlimited stakeholders to future audits" — shown to activated orgs whose
 * invites were failing BECAUSE they had activated. The server now only refuses
 * pre-activation trials, and this copy states the real scope of the counter.
 */
export function inviteCapCopy(error?: CapError): { title: string; body: string } {
  // Branch on the raw server code; `kind` collapses into `cap_kind` when the
  // server sends one, so it cannot tell these states apart.
  const signal = error?.code ?? error?.kind;
  const hasNumbers = typeof error?.used === 'number' && typeof error?.cap === 'number';

  if (signal === 'pre_activation_expired') {
    return {
      title: 'Your free pre-activation period has ended.',
      body: 'Activate a VibeControl trial to keep inviting stakeholders into your blueprints.',
    };
  }

  // Issue #1324 (N1): an org with no pre-activation sponsorship never had free
  // slots. Saying it "used them all" would be a new false claim.
  if (signal === 'invite_not_entitled') {
    return {
      title: 'Stakeholder invites need a VibeControl trial or plan.',
      body: 'Your organization does not have free invitation slots. Activating a VibeControl trial lets you invite your team into this blueprint.',
    };
  }

  return {
    title: hasNumbers
      ? `You've used all ${error!.cap} of your organization's free invitation slots.`
      : "You've used your organization's free invitation slots.",
    body: 'Invite slots are counted across your whole organization for the lifetime of your free access, and a slot is used up when you send — revoking an invite does not return one. Activating a VibeControl trial removes the limit.',
  };
}

export function CapExceededModal(props: Props) {
  const isInvite = props.kind === 'invite';
  // Issue #1221 — `aria-modal` alone is a promise the markup never kept:
  // focus stayed on the page behind the dialog and Tab walked straight out
  // of it. This moves focus in, cycles it inside, closes on Escape, and
  // hands focus back to whatever opened the dialog.
  const dialogRef = useDialogFocus<HTMLDivElement>(props.onClose);

  const title = isInvite
    ? inviteCapCopy(props.error).title
    : getCapCopy(props.error).title;
  const body = isInvite
    ? inviteCapCopy(props.error).body
    : getCapCopy(props.error).body;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cap-modal-title"
      aria-describedby="cap-modal-body"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="relative bg-card border border-line rounded-2xl shadow-soft max-w-md w-full p-8 flex flex-col gap-4">
        <h2 id="cap-modal-title" className="text-lg font-semibold text-ink">
          {title}
        </h2>
        <p id="cap-modal-body" className="text-sm text-graphite">{body}</p>

        {isInvite ? (
          <Link
            to={`/audits/${props.auditId}/materialize`}
            className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-plum text-[#0B0E14] px-4 py-2 text-sm font-semibold hover:opacity-90 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
          >
            Activate VibeControl trial
          </Link>
        ) : (
          getCapCopy(props.error).cta && (
            <a
              href={getCapCopy(props.error).cta!.href}
              className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-plum text-[#0B0E14] px-4 py-2 text-sm font-semibold hover:opacity-90 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
            >
              {getCapCopy(props.error).cta!.label}
            </a>
          )
        )}

        <button
          type="button"
          onClick={props.onClose}
          className="mt-1 inline-flex min-h-[44px] items-center justify-center rounded-xl text-sm font-medium text-graphite hover:text-ink transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
        >
          Close
        </button>
      </div>
    </div>
  );
}
